import React, { useEffect, useRef } from 'react';
import Alert from '../../../../../../../../../../shared/design-system/ui/alert';
import AlertCircleRedIcon from '../../../../../../../../../../assets/images/alert-circle-red.svg';

interface SuggestedPhoneNumbersTableProps {
  columns: {
    key: string;
    header: string | JSX.Element;
    component: (row: any) => JSX.Element;
  }[];
  data: any[];
  tableTitle: JSX.Element;
  actionComponent?: JSX.Element;
  className?: string;
  rowClassName?: (row: any, rowIndex: number) => string;
  tableHeightClassname?: string;
  cartCount?: number;
}

const SuggestedPhoneNumbersTable: React.FC<SuggestedPhoneNumbersTableProps> = ({
  columns = [],
  data = [],
  tableTitle = null,
  actionComponent = null,
  className = '',
  rowClassName = () => '',
  tableHeightClassname = '',
  cartCount = 0,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateBannerHeight = () => {
      const banners = document.querySelectorAll('[data-banner]');
      const totalBannerHeight = Array.from(banners).reduce(
        (total, banner) => total + banner.getBoundingClientRect().height,
        0,
      );
      document.documentElement.style.setProperty(
        '--banner-height',
        `${totalBannerHeight}px`,
      );
    };

    updateBannerHeight();

    window.addEventListener('resize', updateBannerHeight);
    return () => {
      window.removeEventListener('resize', updateBannerHeight);
    };
  }, [cartCount]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const handleScroll = () => {
      // Toggle 'scrolled' class based on scroll position (1px threshold)
      scrollContainer.classList.toggle('scrolled', scrollContainer.scrollTop >= 1);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    // Check initial scroll position
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="suggested-phone-numbers-container">
      <div className="suggested-phone-numbers">
        <div className="header">
          {tableTitle}
          {actionComponent}
        </div>
        {cartCount > 9 && (
          <Alert
            className="purchase-limit-reached-alert"
            variant="alert"
            description={
              <div className="purchase-limit-alert-content">
                <img
                  src={AlertCircleRedIcon}
                  alt="Alert"
                  width={16}
                  height={16}
                />
                <p>Purchase limit reached. You can only buy 10 numbers at once.</p>
              </div>
            }
          />
        )}
        <div 
          ref={scrollContainerRef}
          className={`suggested-phone-numbers-table-outer-container ${tableHeightClassname}`}
        >
          <div className="suggested-phone-numbers-table-container">
            <table className={`phone-numbers-table ${className}`}>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key}>
                      <span>{column.header}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIndex) => {
                  // Use phoneNumber as key if available, fallback to rowIndex
                  const rowKey = row?.phoneNumber || `row-${rowIndex}`;
                  return (
                    <tr
                      key={rowKey}
                      className={`table-row ${rowClassName(row, rowIndex)}`}
                    >
                      {columns.map((column) => (
                        <td key={column.key}>{column.component(row)}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestedPhoneNumbersTable;

